import { useCallback, useEffect, useState } from "react";
import consumer from "@/lib/cable";
import type { BizminerAdminInsightRequest } from "@/types/serializers";

interface AdminInsightRequestUpdate {
  insight_request: BizminerAdminInsightRequest;
}

interface UseAdminInsightRequestsChannelOptions {
  enabled?: boolean;
}

interface UseAdminInsightRequestsChannelReturn {
  isConnected: boolean;
}

export function useAdminInsightRequestsChannel(
  onUpdate: (insightRequest: BizminerAdminInsightRequest) => void,
  options: UseAdminInsightRequestsChannelOptions = {},
): UseAdminInsightRequestsChannelReturn {
  const { enabled = true } = options;
  const [isConnected, setIsConnected] = useState(false);
  const stableOnUpdate = useCallback(onUpdate, []);

  useEffect(() => {
    if (!consumer || !enabled) {
      setIsConnected(false);
      return;
    }

    console.log("[AdminInsightRequestsChannel] Subscribing...");

    const subscription = consumer.subscriptions.create(
      { channel: "Bizminer::Admin::InsightRequestsChannel" },
      {
        connected() {
          console.log("[AdminInsightRequestsChannel] Connected");
          setIsConnected(true);
        },
        disconnected() {
          console.log("[AdminInsightRequestsChannel] Disconnected");
          setIsConnected(false);
        },
        rejected() {
          console.log("[AdminInsightRequestsChannel] Subscription rejected");
          setIsConnected(false);
        },
        received(data: AdminInsightRequestUpdate) {
          console.log("[AdminInsightRequestsChannel] Received update:", data);
          stableOnUpdate(data.insight_request);
        },
      },
    );

    return () => {
      console.log("[AdminInsightRequestsChannel] Unsubscribing...");
      subscription.unsubscribe();
      setIsConnected(false);
    };
  }, [stableOnUpdate, enabled]);

  return { isConnected };
}
